import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@repo/go-table';
import axios, { type AxiosInstance } from 'axios';
import { format, parseISO } from 'date-fns';
import { action, flow, makeObservable, observable } from 'mobx';

import { EventEmitter } from '../../shared/EventEmitter';
import type { IDirectoriesService, TDictObject } from '../../shared/IDirectoriesService';
import type { INotificationsService } from '../../shared/INotificationsService';
import { SockClientService } from '../../shared/SockClientService';
import { TimeZonesStore } from '../../shared/stores/TimeZonesStore';
import type { Wellbores } from '../../shared/stores/WellboresStore';
import type { SettingsStore } from '../stores/SettingsStore';

import type { TPageResponse } from './AutoAnomaliesApi';
import { AutoAnomaliesApi } from './AutoAnomaliesApi';
import { isApplicableFilterItem } from './filter-utils';
import { AutoAnomaliesUiState } from './ManualAnomaliesUiState';
import type { TAutoAnomalyRowData, TAutoAnomalyRowDto } from './types';

type TAutoAnomaliesMessage = { type: 'DATA_CHANGED' };

const isFilterModelsEqual = (a: GridFilterModel, b: GridFilterModel): boolean => {
  return JSON.stringify(a) === JSON.stringify(b);
};

const mapSourceOption = (sourceData: TAutoAnomalyRowDto['sourceData']): TAutoAnomalyRowData['sourceOption'] => {
  switch (sourceData.type) {
    case 'GENERAL_PARAMETERS':
      return sourceData.param;
    case 'AUTOSUPERVISOR_OPERATIONS':
    case 'AUTOSUPERVISOR_KPI_STATES':
      return sourceData.code;
    case 'DRILLING_LOADS':
      return sourceData.mnemonic;
    default:
      sourceData satisfies never;
      return undefined;
  }
};

const mapAutoAnomalyRowDtoToData = (dto: TAutoAnomalyRowDto): TAutoAnomalyRowData => {
  return {
    id: dto.id,
    wellId: dto.wellId,
    wellboreId: dto.wellboreId,
    indexType: dto.indexType,
    riskLevel: dto.riskLevel,
    anomalyTypeId: dto.anomalyType?.typeId,
    anomalySubtypeId: dto.anomalyType?.subtypeId,
    sourceType: dto.sourceData.type,
    sourceOption: mapSourceOption(dto.sourceData),
    bitDepth: dto.bitDepth,
    holeDepth: dto.holeDepth,
    unitId: dto.unitId,
    updatedAt: dto.updatedAt,
    updatedBy: dto.updatedBy,
    createdAt: dto.createdAt,
    createdBy: dto.createdBy,
    anomalyAt: dto.anomalyAt,
    notification: dto.notification,
    recommendation: dto.recommendation,
    minimumReferenceCurve: dto.minimumReferenceCurve,
    maximumReferenceCurve: dto.maximumReferenceCurve,
    stageTypeId: dto.stageTypeId,
    wellboreTypeId: dto.wellboreTypeId,
    trajectoryTypeId: dto.trajectoryTypeId,
    sectionTypeId: dto.sectionTypeId,
    stratUnitTypeId: dto.stratUnitTypeId,
    reservoirId: dto.reservoirId,
    runTypeId: dto.runTypeId,
    operationTypeId: dto.operationTypeId,
    limitType: dto.limitType,
    valueLimit: dto.values?.limit,
    valueFact: dto.values?.fact,
    valueAbsoluteDiff: dto.values?.absoluteDiff,
    valueRelativeDiff: dto.values?.relativeDiff,
    holeType: dto.holeType,
  };
};

type TEventMap = {
  newAutoAnomalies: TAutoAnomalyRowData[];
  deleteAutoAnomalies: string[];
  updateAutoAnomalies: TAutoAnomalyRowData[];
};

export class AutoAnomaliesService {
  private readonly api: AutoAnomaliesApi;
  private readonly abortControllers = new Map<string, AbortController>();
  private readonly socket: SockClientService<TAutoAnomaliesMessage>;
  readonly uiState = new AutoAnomaliesUiState();
  readonly eventEmitter = new EventEmitter<TEventMap>();

  topicAutoAnomalies;

  @observable loading = false;
  @observable hasUpdates = false;
  @observable.ref rows: TAutoAnomalyRowData[] = [];
  @observable rowCount = 0;
  @observable.ref paginationModel: GridPaginationModel;
  @observable.ref sortModel: GridSortModel;
  @observable.ref filterModel: GridFilterModel;

  @observable anomalyTypes: TDictObject[] = [];
  @observable anomalySubtypes: TDictObject[] = [];
  @observable workClassifierStageTypes: TDictObject[] = [];
  @observable wellboreTypes: TDictObject[] = [];
  @observable trajectoryTypes: TDictObject[] = [];
  @observable sectionTypes: TDictObject[] = [];
  @observable stratUnitTypes: TDictObject[] = [];
  @observable reservoirTypes: TDictObject[] = [];
  @observable runTypes: TDictObject[] = [];
  @observable unitsList: TDictObject[] = [];
  @observable parameterTypes: TDictObject[] = [];
  @observable autosupervisorOperations: TDictObject[] = [];
  @observable autosupervisorKPIstates: TDictObject[] = [];

  @observable areDirectoriesLoaded = false;

  constructor(
    private readonly wellId: number,
    agent: AxiosInstance,
    public readonly settingsStore: SettingsStore,
    private readonly timeZonesStore: TimeZonesStore,
    public readonly wellbores: Wellbores,
    private readonly directoryService: IDirectoriesService,
    private readonly notifications: INotificationsService,
    getAccessToken: () => string | null,
  ) {
    this.api = new AutoAnomaliesApi(agent);
    this.topicAutoAnomalies = `/topic/anomalies/well/${wellId}/auto`;
    this.socket = new SockClientService<TAutoAnomaliesMessage>('/anomaly-registry/api/ws', getAccessToken);
    this.paginationModel = this.settingsStore.autoAnomaliesSettings.paginationModel ?? { page: 0, pageSize: 25 };
    this.sortModel = this.settingsStore.autoAnomaliesSettings.sortingModel ?? [{ field: 'createdAt', sort: 'desc' }];
    this.filterModel = this.settingsStore.autoAnomaliesSettings.filterModel ?? { items: [] };
    makeObservable(this);
  }

  setEditing(value: boolean) {
    this.uiState.setEditing(value);
  }

  @flow.bound
  private async *fetchDirectories() {
    try {
      const [
        anomalyTypes,
        anomalySubtypes,
        workClassifierStageTypes,
        wellboreTypes,
        trajectoryTypes,
        sectionTypes,
        stratUnitTypes,
        reservoirTypes,
        runTypes,
        unitsList,
        parameterTypes,
        autosupervisorOperations,
        autosupervisorKPIstates,
      ] = await this.directoryService.loadObjects([
        'Common_AnomalyTypes',
        'Common_AnomalySubTypes',
        'GOreport_WorkClassifier_Stage',
        'Common_WellboreType',
        'Common_TrajectoryType',
        'Common_SectionType',
        'Common_StratUnitType',
        'Common_Reservoir',
        'Common_RunType',
        'Common_Uom',
        'Common_Parameter',
        'Common_AutosupervisorOperations',
        'Common_AutosupervisorKPIstates',
      ]);
      yield;

      this.anomalyTypes = anomalyTypes ?? [];
      this.anomalySubtypes = anomalySubtypes ?? [];
      this.workClassifierStageTypes = workClassifierStageTypes ?? [];
      this.wellboreTypes = wellboreTypes ?? [];
      this.trajectoryTypes = trajectoryTypes ?? [];
      this.sectionTypes = sectionTypes ?? [];
      this.stratUnitTypes = stratUnitTypes ?? [];
      this.reservoirTypes = reservoirTypes ?? [];
      this.runTypes = runTypes ?? [];
      this.unitsList = unitsList ?? [];
      this.parameterTypes = parameterTypes ?? [];
      this.autosupervisorOperations = autosupervisorOperations ?? [];
      this.autosupervisorKPIstates = autosupervisorKPIstates ?? [];

      this.areDirectoriesLoaded = true;
    } catch (e) {
      yield;
      console.error(e);
    }
  }

  @action.bound
  setPaginationModel(model: GridPaginationModel) {
    this.paginationModel = model;
    this.settingsStore.setAutoAnomaliesSettings({
      ...this.settingsStore.autoAnomaliesSettings,
      paginationModel: this.paginationModel,
    });
    this.loadPage();
  }

  private getActiveTimeZoneOffset(): string {
    const { isNegative, wholeHours, minutes } = TimeZonesStore.getHours(this.timeZonesStore.selected);
    const sign = isNegative ? '-' : '+';

    return `${sign}${String(wholeHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private getFilterModelForRequest(filterModel: GridFilterModel = this.filterModel): GridFilterModel {
    return {
      ...filterModel,
      items: filterModel.items?.filter(isApplicableFilterItem).map((item) => {
        if (item.field !== 'anomalyAt' || item.value == null || item.value === '') {
          return item;
        }

        // FilterItem для type: 'date' пишет только день ('2026-08-22').
        // parseISO читает его как локальный день, иначе new Date прочитает UTC и 22.08 будет равно 21.08.
        // Для type: 'dateTime' FilterItem уже пишет полный ISO, этот разбор не нужен.
        const rawValue = String(item.value);
        const parsedValue = item.value instanceof Date ? item.value : parseISO(rawValue);
        const dateValue = Number.isNaN(parsedValue.valueOf()) ? new Date(rawValue) : parsedValue;

        if (Number.isNaN(dateValue.valueOf())) {
          return item;
        }

        return {
          ...item,
          value: format(dateValue, `yyyy-MM-dd'T'00:00:00'${this.getActiveTimeZoneOffset()}'`),
        };
      }),
    };
  }

  @action.bound
  setSortModel(model: GridSortModel) {
    this.sortModel = model;
    this.paginationModel = { ...this.paginationModel, page: 0 };
    this.settingsStore.setAutoAnomaliesSettings({
      ...this.settingsStore.autoAnomaliesSettings,
      paginationModel: this.paginationModel,
      sortingModel: model,
    });
    this.loadPage();
  }

  @action.bound
  setFilterModel(model: GridFilterModel) {
    const prevRequestFilterModel = this.getFilterModelForRequest(this.filterModel);
    this.filterModel = model;
    const nextRequestFilterModel = this.getFilterModelForRequest(model);

    if (!isFilterModelsEqual(prevRequestFilterModel, nextRequestFilterModel)) {
      this.paginationModel = { ...this.paginationModel, page: 0 };
      this.settingsStore.setAutoAnomaliesSettings({
        ...this.settingsStore.autoAnomaliesSettings,
        paginationModel: this.paginationModel,
        filterModel: model,
      });
      this.loadPage();
    }
  }

  prevTime = 0;

  @flow.bound
  *loadPage() {
    const updatesVersion = this.updatesVersion;
    this.prevTime = Date.now();
    this.loading = true;
    this.abortControllers.get('fetchPage')?.abort();
    const ac = new AbortController();
    this.abortControllers.set('fetchPage', ac);

    try {
      const list: TPageResponse<TAutoAnomalyRowDto> = yield this.api.fetchList(
        {
          wellId: this.wellId,
          page: this.paginationModel.page,
          size: this.paginationModel.pageSize,
          sort: this.sortModel,
          filter: this.getFilterModelForRequest(),
        },
        ac.signal,
      );
      this.rowCount = list.totalElements;
      this.setAnomalies(list.content);
      this.hasUpdates = updatesVersion !== this.updatesVersion;
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }
      this.notifications.showErrorMessageT('alertsManagerWidget:errorMessages.fetchAutoAnomalies');
      console.error(e);
    } finally {
      this.loading = false;
      this.abortControllers.delete('fetchPage');
    }
  }

  hasUnsavedChanges = false;
  private updatesVersion = 0;

  @flow.bound
  *deleteAutoAnomaliesIds(ids: string[]) {
    this.loading = true;
    try {
      yield this.api.deleteRows(ids).catch((e) => {
        if (!axios.isCancel(e)) {
          console.error(e);
          this.notifications.showErrorMessageT('alertsManagerWidget:errorMessages.deleteAutoAnomalies');
        }
      });
      yield this.loadPage();
    } catch (e) {
      if (!axios.isCancel(e)) {
        console.error(e);
      }
    }
  }

  discardChanges() {
    // noop
  }

  setAnomalies(list: TAutoAnomalyRowDto[]) {
    const mappedList = list.map(mapAutoAnomalyRowDtoToData);
    this.rows = mappedList;
    this.eventEmitter.emit('newAutoAnomalies', mappedList);
  }

  autoAnomaliesMassageCallback = (data: TAutoAnomaliesMessage) => {
    if (data.type === 'DATA_CHANGED') {
      this.updatesVersion += 1;
      this.hasUpdates = true;
    }
  };

  async loadInitialData() {
    await Promise.all([this.loadPage(), this.fetchDirectories(), this.wellbores.fetchWellbores(this.wellId)]);
  }

  init = (): VoidFunction => {
    void this.loadInitialData();

    this.socket.init(true);
    this.socket.subscribe(this.topicAutoAnomalies, this.autoAnomaliesMassageCallback);

    return () => {
      this.abortControllers.forEach((c) => c.abort());
      this.socket.unsubscribe(this.topicAutoAnomalies, this.autoAnomaliesMassageCallback);
      this.socket.destroy();
    };
  };
}
